import React from "react";
import {
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useDisclosure,
  Image,
  Stack,
  useBreakpointValue,
} from "@chakra-ui/react";
import questionIcon from "../../assets/images/question.svg";

const features = [
  "Wide-angle shots",
  "Time on-site",
  "Priority shots",
  "Landscape mode",
  "Secondary shots",
  "Communal areas",
  "Exterior shots",
  "Image selection",
  "Purchase extra images",
  "Creative shots",
  "Decluttering",
  "Portrait mode",
];

const packages = [
  {
    name: "Budget",
    color: "linear-gradient(90deg, #F0D5C1 0%, #FFF4ED 47.6%, #F0D5C1 100%)",
    values: ["10", "≤ 45min", "✓", "✓", "✓", "On request", "On request", "", "", "", "", ""]
  },
  {
    name: "Standard",
    color: "linear-gradient(90deg, #D5D5D6 0%, #FBFBFB 47.6%, #D5D5D6 100%)",
    values: ["15", "≤ 1.5h", "✓", "✓", "✓", "On request", "✓", "✓", "✓", "", "", ""]
  },
  {
    name: "Premium",
    color: "linear-gradient(90deg, #DCB348 0%, #FFF597 47.6%, #DCB348 100%)",
    values: ["25", "≤ 2.5h", "✓", "✓", "✓", "On request", "✓", "✓", "✓", "✓", "✓", "✓"],
    border: true
  }
];

const PhotographyPackagesModal = ({ description, heading, isBundle = false }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const isPhotography =
      heading === "Property Photography" ||
      (heading.includes("Photos") && !heading.includes("VT Photos"));
  const isMobile = useBreakpointValue({ base: true, md: false });

  if (!description || description.trim() === "") return null;

  const PhotographyContent = (
      <>
        {isMobile ? (
            <Stack spacing={6}>
              {packages.map((pack, pIdx) => (
                  <Box
                      key={pIdx}
                      border="1px solid #ECEDEB"
                      borderRadius="8px"
                      overflow="hidden"
                      outline={pack.border ? "2px solid #F1DA77" : undefined}
                      outlineOffset="-1px"
                  >
                    <Box
                        h="46px"
                        bg={pack.color}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        fontWeight="bold"
                        fontSize="16px"
                    >
                      {pack.name}
                    </Box>
                    {features.map((feature, idx) => (
                        <Box
                            key={idx}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            px={4}
                            h="38px"
                            fontSize="14px"
                            borderBottom="1px solid #ECEDEB"
                        >
                          <Text color="#2D2D2D">{feature}</Text>
                          <Text
                              color={pack.name === "Premium" ? "#8C6B1F" : "#2D2D2D"}
                              fontWeight={pack.name === "Premium" ? 500 : 400}
                          >
                            {pack.values[idx]}
                          </Text>
                        </Box>
                    ))}
                  </Box>
              ))}
              <Stack spacing={3} mt={4} fontSize="sm" color="#666">
                <Text><b>*Priority shots</b> – Images towards windows of Bedrooms, Living room, Kitchen, Bathrooms/Shower rooms and outward facing shots for Balconies or Terraces.</Text>
                <Text><b>*Secondary shots</b> – Views from the apartment, utility rooms, storage spaces, hallways, and standalone toilets. Exterior shots include garden images, garage, and the back of the house.</Text>
                <Text><b>*Communal areas</b> – Gym, spa, cinema, concierge, and others are only photographed when access is arranged by the client and requested at the time of booking.</Text>
                <Text><b>*Decluttering</b> – Decluttering removes visual distractions like personal items and clutter to make spaces look clean and spacious in photos. It’s not staging or cleaning, just photo prep, with up to 1 hour allocated.</Text>
              </Stack>
            </Stack>
        ) : (
            <Box>
              <Box
                  display="flex"
                  alignItems="start"
                  border="1px solid #ECEDEB"
                  borderRadius="8px"
                  overflow="hidden"
                  w="628px"
                  mx="auto"
              >
                <VStack spacing={0} w="176px" bg="#F9F9F9" mt="46px">
                  {features.map((feature, idx) => (
                      <Box
                          key={idx}
                          h="38px"
                          display="flex"
                          alignItems="center"
                          justifyContent="flex-start"
                          px={4}
                          w="full"
                          fontSize="14px"
                          fontWeight="500"
                          borderBottom="1px solid #ECEDEB"
                      >
                        {feature}
                      </Box>
                  ))}
                </VStack>
                {packages.map((pack, pIdx) => (
                    <VStack
                        key={pIdx}
                        spacing={0}
                        w="150px"
                        borderLeft="1px solid #ECEDEB"
                        borderTopRightRadius={pack.border ? "8px" : undefined}
                        borderBottomRightRadius={pack.border ? "8px" : undefined}
                        outline={pack.border ? "2px solid #F1DA77" : undefined}
                        outlineOffset="-1px"
                        overflow="hidden"
                    >
                      <Box
                          h="46px"
                          w="full"
                          bg={pack.color}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          fontWeight="bold"
                          fontSize="16px"
                          borderTopRightRadius={pack.border ? "8px" : undefined}
                      >
                        {pack.name}
                      </Box>
                      {features.map((_, idx) => (
                          <Box
                              key={idx}
                              h="38px"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              px={4}
                              w="full"
                              fontSize="14px"
                              color={pack.name === "Premium" ? "#be9026" : "#2D2D2D"}
                              fontWeight={pack.name === "Premium" ? 500 : 400}
                              borderBottom="1px solid #ECEDEB"
                          >
                            {pack.values[idx] === "✓" && "✓"}
                            {pack.values[idx] === "On request" && "On request"}
                            {pack.values[idx] !== "✓" && pack.values[idx] !== "On request" && pack.values[idx]}
                          </Box>
                      ))}
                    </VStack>
                ))}
              </Box>
              <Stack spacing={3} mt={4} fontSize="sm" color="#666">
                <Text><b>*Priority shots</b> – Images of bedroom, living room, kitchen, and bathroom/shower room windows, as well as outward-facing shots of balconies or terraces.</Text>
                <Text><b>*Secondary shots</b> – Views from the apartment, utility rooms, storage spaces, hallways, and standalone toilets. Exterior shots include garden images, garage, and the back of the house.</Text>
                <Text><b>*Communal areas</b> – Gym, spa, cinema, concierge, and others are only photographed when access is arranged by the client and requested at the time of booking.</Text>
                <Text><b>*Decluttering</b> – Decluttering removes visual distractions like personal items and clutter to make spaces look clean and spacious in photos. It’s not staging or cleaning, just photo prep, with up to 1 hour allocated.</Text>
              </Stack>
            </Box>
        )}
        {heading !== "Property Photography" && (
            <Box mt={6}>
              <div dangerouslySetInnerHTML={{ __html: description }} />
            </Box>
        )}
      </>
  );

  return (
      <>
        <Box
            as="button"
            onClick={(e) => {
              e.stopPropagation();
              onOpen();
            }}
            position="absolute"
            top="6px"
            right="6px"
            width="21px"
            height="21px"
            zIndex="99995"
        >
          <Image src={questionIcon} alt="Question" width="21px" height="21px" />
        </Box>

        <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
          <ModalOverlay />
          <ModalContent borderRadius="10px" bg="white" maxW="726px" containerProps={{ zIndex: '99995' }}>
            <ModalHeader fontSize="25px"> {(isPhotography || heading === 'Floor Plan + EPC (package)') ? heading : ''} </ModalHeader>

            {isPhotography && (
                <Box as="ul" pl={6} pr={6} mt="-12px" mb="8px" fontSize="16px">
                  <span>
                    Estimated time at the property 1h - 4.5h depending on property size and the photography service selected.
                    For individual times for each service please see the description below.
                  </span>
                </Box>
            )}

            <ModalCloseButton />
            <ModalBody>
              {isPhotography ? (
                  <>
                    {isBundle && (
                        <Text as="h4" fontSize="20px" fontWeight="bold" mb={3}>
                          Property Photography
                        </Text>
                    )}
                    {PhotographyContent}
                  </>
              ) : (
                  <div dangerouslySetInnerHTML={{ __html: description }} />
              )}
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
  );
};

export default PhotographyPackagesModal;
